// Direct-to-S3 ("v2") proof file uploads.
//
// Flow: POST presign -> XHR PUT straight to S3 -> POST complete. The API only
// brokers; the file bytes never pass through an API pod. The legacy multipart
// PUT in ProofFileService.UploadFile stays as the fallback, so this service
// must answer `{ handled: false }` whenever the v2 path is unavailable
// (feature flag off, old API without the routes, or the server says the
// feature is disabled) and the caller runs the legacy code unchanged.
//
// Once a PUT has been attempted the bytes may already be in S3 and the server
// may finalize the row on its own (S3 event). From that point on this service
// never falls back to the legacy path on its own judgement: only the server's
// abort answer (204 = row gone) after a pure network failure allows it. Every
// other outcome is resolved against the server (complete, then status poll).
//
// Progress contract (same `fileObj` the legacy path mutates):
//   0-90   real bytes acknowledged by S3 (xhr.upload.onprogress)
//   90-100 the complete call (finalize) is running
//   100    the row exists; fileObj.id is set, uploadCompleteCallback() fired
//
// The presigned URL is signed for the exact Content-Type the API returns in
// `headers`, so the PUT must echo that value, not file.type.

import EnvService from "../env.settings";

const PUT_RETRY_DELAYS_MS = [1000, 3000, 9000];
const COMPLETE_RETRY_DELAYS_MS = [2000, 5000, 10000];
const FINALIZING_RETRY_DELAY_MS = 1500;
const FINALIZING_MAX_ATTEMPTS = 10;
const STATUS_POLL_ATTEMPTS = 3;
const STATUS_POLL_DELAY_MS = 2000;
const HEARTBEAT_INTERVAL_MS = 10 * 60 * 1000;
const UPLOAD_PROGRESS_CEILING = 90;
// ProofVersionFile.uploadStatus: 0 pending, 1 uploaded, 2 processing, 3 processed, 4 failed
const UPLOAD_STATUS_FAILED = 4;
const UPLOAD_STATUS_SETTLED = [1, 2, 3];

function generateClientUploadId() {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers / test envs.
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

class S3PutError extends Error {
  constructor(message, status) {
    super(message);
    this.name = "S3PutError";
    // 0 = network error / abort (no HTTP status)
    this.status = status;
  }
}

function putToS3(url, file, contentType, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url, true);
    xhr.setRequestHeader("Content-Type", contentType);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && typeof onProgress === "function") {
        onProgress(e.loaded, e.total);
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve({ etag: xhr.getResponseHeader("ETag") || null });
      } else {
        reject(new S3PutError(`S3 upload failed (${xhr.status})`, xhr.status));
      }
    };
    xhr.onerror = () =>
      reject(new S3PutError("S3 upload failed (network error)", 0));
    xhr.onabort = () => reject(new S3PutError("S3 upload aborted", 0));
    xhr.send(file);
  });
}

function responseCode(e) {
  return e && e.response && e.response.data && e.response.data.code;
}

function responseStatus(e) {
  return e && e.response && e.response.status;
}

function responseMessage(e) {
  const data = e && e.response && e.response.data;
  if (data && data.message !== undefined && data.message !== null)
    return String(data.message);
  return "";
}

function responseFile(e) {
  const data = e && e.response && e.response.data;
  return data && data.file && data.file.id > 0 ? data.file : null;
}

// Timeout, network error or 5xx: the server may or may not have acted.
function isTransient(e) {
  const status = responseStatus(e);
  return !status || status >= 500;
}

class DirectUploadService {
  constructor() {
    // Set once per page session when the API answers 404 / direct_uploads_disabled,
    // so later files skip the presign probe and go straight to the legacy PUT.
    this.disabledForSession = false;
    this.disabledReason = "";
  }

  isEnabled() {
    return !!EnvService.instance.directUploads && !this.disabledForSession;
  }

  _disableForSession(reason) {
    this.disabledForSession = true;
    this.disabledReason = reason;
    console.info(
      "upload.fallback reason=" +
        reason +
        " (direct uploads disabled for this session)"
    );
  }

  _headers() {
    return {
      "Cache-Control": "no-cache",
      "Content-Type": "application/json; charset=utf-8",
      AccessToken: window.$A.Auth.apiToken.token,
    };
  }

  _base(proofId, proofVersionId) {
    return (
      "/v2/proof/" + proofId + "/proof-version/" + proofVersionId + "/uploads"
    );
  }

  async _presign(proofId, proofVersionId, fileObj) {
    const body = {
      fileName: fileObj.file.name,
      contentType: fileObj.file.type || "",
      sizeBytes: fileObj.file.size,
      clientUploadId: fileObj.clientUploadId,
    };
    const response = await window.$A.Api.post(
      this._base(proofId, proofVersionId),
      body,
      {
        headers: this._headers(),
        timeout: 30000,
      }
    );
    return response.data;
  }

  async _renew(proofId, proofVersionId, uploadId) {
    const response = await window.$A.Api.post(
      this._base(proofId, proofVersionId) + "/" + uploadId + "/renew",
      {},
      { headers: this._headers(), timeout: 30000 }
    );
    return response.data;
  }

  async _complete(proofId, proofVersionId, uploadId) {
    const response = await window.$A.Api.post(
      this._base(proofId, proofVersionId) + "/" + uploadId + "/complete",
      {},
      { headers: this._headers(), timeout: 120000 }
    );
    return response.data;
  }

  async _status(uploadId) {
    const response = await window.$A.Api.get(
      "/v2/proof-version-file/" + uploadId,
      { headers: this._headers(), timeout: 30000 }
    );
    return response.data;
  }

  // Resolves to one of:
  //   { outcome: "aborted" }          204: the row is gone, nothing can finalize it later
  //   { outcome: "finalized", file }  409 already_completed: the bytes landed and the
  //                                   server finalized the row (a success)
  //   { outcome: "unknown" }          anything else
  async _abort(proofId, proofVersionId, uploadId) {
    try {
      await window.$A.Api.post(
        this._base(proofId, proofVersionId) + "/" + uploadId + "/abort",
        {},
        { headers: this._headers(), timeout: 15000 }
      );
      return { outcome: "aborted" };
    } catch (e) {
      const file = responseFile(e);
      if (
        responseStatus(e) === 409 &&
        responseCode(e) === "already_completed" &&
        file
      ) {
        return { outcome: "finalized", file };
      }
      console.info("upload.abort failed uploadId=" + uploadId);
      return { outcome: "unknown" };
    }
  }

  // PUT with retries. Network errors retry the same URL with backoff; a 403
  // (expired or bad signature) renews the URL once. A heartbeat renew runs
  // every 10 minutes while the transfer is in flight so the server keeps the
  // pending row alive. If any renew answers 410 the server finalized the row
  // on its own (S3 event); the in-flight PUT is left to finish and the caller
  // goes straight to complete. Resolves when the bytes are in S3 or when a
  // renew failure says complete should decide; throws S3PutError otherwise.
  async _putWithRetries(proofId, proofVersionId, presign, fileObj, state) {
    let url = presign.url;
    let contentType =
      (presign.headers && presign.headers["Content-Type"]) ||
      fileObj.file.type ||
      "";
    let renewed = false;
    let networkAttempt = 0;
    const onProgress = (loaded, total) => {
      if (!total) return;
      const pct = Math.round((loaded * UPLOAD_PROGRESS_CEILING) / total);
      fileObj.uploadProgress = Math.min(
        UPLOAD_PROGRESS_CEILING,
        Math.max(1, pct)
      );
    };
    const heartbeat = setInterval(async () => {
      try {
        await this._renew(proofId, proofVersionId, presign.uploadId);
      } catch (e) {
        if (responseStatus(e) === 410) {
          state.finalizedElsewhere = true;
          console.info(
            "upload.direct heartbeat uploadId=" +
              presign.uploadId +
              " finalized-elsewhere"
          );
        }
      }
    }, HEARTBEAT_INTERVAL_MS);
    try {
      for (;;) {
        if (state.finalizedElsewhere && networkAttempt > 0) {
          // A retry would only race the server; let complete decide.
          return { uploaded: false };
        }
        try {
          await putToS3(url, fileObj.file, contentType, onProgress);
          return { uploaded: true };
        } catch (e) {
          if (e instanceof S3PutError && e.status === 403 && !renewed) {
            renewed = true;
            let fresh;
            try {
              fresh = await this._renew(
                proofId,
                proofVersionId,
                presign.uploadId
              );
            } catch (renewError) {
              // The server would not renew (e.g. 410: it already finalized the
              // row from the S3 event). Stop sending bytes; complete decides.
              state.renewFailed = true;
              if (responseStatus(renewError) === 410)
                state.finalizedElsewhere = true;
              console.info(
                "upload.direct renew failed uploadId=" +
                  presign.uploadId +
                  " status=" +
                  (responseStatus(renewError) || "none") +
                  " (proceeding to complete)"
              );
              return { uploaded: false };
            }
            url = fresh.url || url;
            if (fresh.headers && fresh.headers["Content-Type"]) {
              contentType = fresh.headers["Content-Type"];
            }
            continue;
          }
          if (
            e instanceof S3PutError &&
            e.status === 0 &&
            networkAttempt < PUT_RETRY_DELAYS_MS.length
          ) {
            const delay = PUT_RETRY_DELAYS_MS[networkAttempt];
            networkAttempt += 1;
            console.info(
              "upload.direct retry uploadId=" +
                presign.uploadId +
                " attempt=" +
                networkAttempt +
                " delayMs=" +
                delay
            );
            await sleep(delay);
            continue;
          }
          throw e;
        }
      }
    } finally {
      clearInterval(heartbeat);
    }
  }

  // Runs the PUT and maps its outcome. Resolves to one of:
  //   { next: "complete" }          bytes are in S3 (or the server said it will decide)
  //   { next: "fallback" }          row aborted after a pure network failure: legacy PUT may run
  //   { next: "done", result }      a final answer (success via abort 409, or failure)
  async _putPhase(
    proofId,
    proofVersionId,
    presign,
    fileObj,
    state,
    startedAt,
    uploadCompleteCallback
  ) {
    let putError;
    try {
      await this._putWithRetries(
        proofId,
        proofVersionId,
        presign,
        fileObj,
        state
      );
      return { next: "complete" };
    } catch (e) {
      putError = e;
    }
    if (state.finalizedElsewhere) {
      // The server already finalized the row; complete returns the finished row.
      return { next: "complete" };
    }
    const abort = await this._abort(proofId, proofVersionId, presign.uploadId);
    if (abort.outcome === "finalized") {
      return {
        next: "done",
        result: this._succeed(
          fileObj,
          abort.file,
          startedAt,
          presign.uploadId,
          uploadCompleteCallback
        ),
      };
    }
    if (
      abort.outcome === "aborted" &&
      putError instanceof S3PutError &&
      putError.status === 0
    ) {
      // The browser could not reach S3 at all and the server dropped the row;
      // the legacy path through the API may still work.
      console.info(
        "upload.fallback reason=s3_unreachable uploadId=" + presign.uploadId
      );
      fileObj.clientUploadId = generateClientUploadId();
      return { next: "fallback" };
    }
    return {
      next: "done",
      result: this._fail(
        fileObj,
        "Upload failed (" +
          (putError && putError.message ? putError.message : "S3 error") +
          ")",
        uploadCompleteCallback
      ),
    };
  }

  // Calls complete until the server gives a definitive answer. Resolves to
  // { result } (final) or { restart: true } (410: start over from presign).
  async _completePhase(
    proofId,
    proofVersionId,
    presign,
    fileObj,
    state,
    startedAt,
    uploadCompleteCallback
  ) {
    let transientAttempt = 0;
    let finalizingAttempt = 0;
    let rePutDone = false;
    for (;;) {
      try {
        const file = await this._complete(
          proofId,
          proofVersionId,
          presign.uploadId
        );
        if (file && file.id !== undefined && file.id > 0) {
          return {
            result: this._succeed(
              fileObj,
              file,
              startedAt,
              presign.uploadId,
              uploadCompleteCallback
            ),
          };
        }
        return {
          result: this._fail(
            fileObj,
            (file && file.message) || "Upload could not be completed",
            uploadCompleteCallback
          ),
        };
      } catch (e) {
        const status = responseStatus(e);
        const code = responseCode(e);
        if (status === 409 && code === "upload_finalizing") {
          // Another finalizer (the S3 event consumer) is mid-way.
          if (finalizingAttempt < FINALIZING_MAX_ATTEMPTS) {
            finalizingAttempt += 1;
            await sleep(FINALIZING_RETRY_DELAY_MS);
            continue;
          }
          break;
        }
        if (status === 409 && code === "upload_incomplete" && !rePutDone) {
          // The object is not in S3 yet: send the bytes once more.
          rePutDone = true;
          const put = await this._putPhase(
            proofId,
            proofVersionId,
            presign,
            fileObj,
            state,
            startedAt,
            uploadCompleteCallback
          );
          if (put.next === "fallback") return { result: { handled: false } };
          if (put.next === "done") return { result: put.result };
          fileObj.uploadProgress = UPLOAD_PROGRESS_CEILING;
          continue;
        }
        if (status === 410 || code === "upload_expired") {
          return { restart: true };
        }
        if (status === 422 || code === "upload_rejected") {
          return {
            result: this._fail(
              fileObj,
              responseMessage(e) || "File was rejected",
              uploadCompleteCallback
            ),
          };
        }
        if (isTransient(e)) {
          if (transientAttempt < COMPLETE_RETRY_DELAYS_MS.length) {
            const delay = COMPLETE_RETRY_DELAYS_MS[transientAttempt];
            transientAttempt += 1;
            console.info(
              "upload.direct complete retry uploadId=" +
                presign.uploadId +
                " attempt=" +
                transientAttempt +
                " delayMs=" +
                delay
            );
            await sleep(delay);
            continue;
          }
          break;
        }
        // Any other 4xx is definitive.
        return {
          result: this._fail(
            fileObj,
            responseMessage(e) ||
              "Upload could not be completed (" + status + ")",
            uploadCompleteCallback
          ),
        };
      }
    }
    // Complete never gave a definitive answer: ask the row itself.
    return {
      result: await this._resolveByPolling(
        presign.uploadId,
        fileObj,
        startedAt,
        uploadCompleteCallback
      ),
    };
  }

  async _resolveByPolling(
    uploadId,
    fileObj,
    startedAt,
    uploadCompleteCallback
  ) {
    for (let attempt = 1; attempt <= STATUS_POLL_ATTEMPTS; attempt += 1) {
      await sleep(STATUS_POLL_DELAY_MS);
      let row = null;
      try {
        row = await this._status(uploadId);
      } catch (e) {
        console.info(
          "upload.direct status poll uploadId=" +
            uploadId +
            " attempt=" +
            attempt +
            " status=" +
            (responseStatus(e) || "none")
        );
      }
      if (!row || !(row.id > 0)) continue;
      if (row.failed) {
        return this._fail(
          fileObj,
          row.conversionMessage || "Upload failed",
          uploadCompleteCallback
        );
      }
      if (UPLOAD_STATUS_SETTLED.indexOf(row.uploadStatus) !== -1) {
        return this._succeed(
          fileObj,
          row,
          startedAt,
          uploadId,
          uploadCompleteCallback
        );
      }
    }
    return this._fail(
      fileObj,
      "Upload could not be completed",
      uploadCompleteCallback
    );
  }

  _fail(fileObj, message, uploadCompleteCallback) {
    // A failed upload never becomes a success on retry: the next attempt
    // presigns a fresh row.
    fileObj.clientUploadId = generateClientUploadId();
    fileObj.error = message || "Upload failed";
    if (fileObj.error === "You Have Run Out Of Files")
      fileObj.noFilesLeft = true;
    fileObj.uploadProgress = 0;
    if (typeof uploadCompleteCallback === "function") uploadCompleteCallback();
    return { handled: true, ok: false };
  }

  _succeed(fileObj, file, startedAt, uploadId, uploadCompleteCallback) {
    fileObj.id = file.id;
    if (typeof uploadCompleteCallback === "function") uploadCompleteCallback();
    fileObj.uploadProgress = 100;
    console.info(
      "upload.direct uploadId=" + uploadId + " ms=" + (Date.now() - startedAt)
    );
    return { handled: true, ok: true };
  }

  // Returns { handled: false } when the caller must run the legacy multipart
  // PUT instead. Returns { handled: true, ok } once the outcome is final.
  async uploadProofFile(
    proofId,
    proofVersionId,
    fileObj,
    uploadCompleteCallback
  ) {
    if (!this.isEnabled()) return { handled: false };
    if (!fileObj || !fileObj.file) return { handled: false };

    const startedAt = Date.now();
    if (!fileObj.clientUploadId)
      fileObj.clientUploadId = generateClientUploadId();
    fileObj.uploadProgress = 0;

    // One restart is allowed when the server reports the upload expired.
    let restarts = 0;
    const restart = (uploadId, reason) => {
      restarts += 1;
      console.info(
        "upload.direct restart uploadId=" + uploadId + " reason=" + reason
      );
      fileObj.clientUploadId = generateClientUploadId();
      fileObj.uploadProgress = 0;
    };
    for (;;) {
      let presign;
      try {
        presign = await this._presign(proofId, proofVersionId, fileObj);
      } catch (e) {
        const status = responseStatus(e);
        const code = responseCode(e);
        if (status === 404 || code === "direct_uploads_disabled") {
          this._disableForSession(code || "route_missing_404");
          return { handled: false };
        }
        if (status === 422 || code === "upload_rejected") {
          return this._fail(
            fileObj,
            responseMessage(e) || "File was rejected",
            uploadCompleteCallback
          );
        }
        if (status === 410) {
          if (restarts < 1) {
            restart("none", code || "gone");
            continue;
          }
          return this._fail(
            fileObj,
            responseMessage(e) || "Upload expired, please try again",
            uploadCompleteCallback
          );
        }
        if (status === 400) {
          return this._fail(
            fileObj,
            responseMessage(e),
            uploadCompleteCallback
          );
        }
        if (status === 401 || status === 403) {
          return this._fail(
            fileObj,
            responseMessage(e) || "Not authorized to upload to this proof",
            uploadCompleteCallback
          );
        }
        // Anything else (API unreachable, 5xx): no bytes were sent yet, so the
        // legacy path may try this file.
        console.info(
          "upload.fallback reason=presign_error status=" + (status || "none")
        );
        return { handled: false };
      }

      // A retry after a lost response: the server already finalized this
      // clientUploadId, so there is nothing to PUT or complete.
      if (presign && !presign.url && presign.file && presign.file.id > 0) {
        if (
          !presign.file.failed &&
          presign.file.uploadStatus !== UPLOAD_STATUS_FAILED
        ) {
          fileObj.id = presign.file.id;
          fileObj.uploadProgress = 100;
          if (typeof uploadCompleteCallback === "function")
            uploadCompleteCallback();
          console.info(
            "upload.direct uploadId=" + presign.uploadId + " already-complete"
          );
          return { handled: true, ok: true };
        }
        return this._fail(
          fileObj,
          presign.file.conversionMessage || "File was rejected",
          uploadCompleteCallback
        );
      }

      if (!presign || !presign.url || !presign.uploadId) {
        console.info("upload.fallback reason=presign_malformed");
        return { handled: false };
      }

      // From here on bytes may reach S3; the server decides the outcome.
      const state = { finalizedElsewhere: false, renewFailed: false };
      const put = await this._putPhase(
        proofId,
        proofVersionId,
        presign,
        fileObj,
        state,
        startedAt,
        uploadCompleteCallback
      );
      if (put.next === "fallback") return { handled: false };
      if (put.next === "done") return put.result;

      fileObj.uploadProgress = UPLOAD_PROGRESS_CEILING;

      // Finalize. The API verifies the object and creates the proof file.
      const done = await this._completePhase(
        proofId,
        proofVersionId,
        presign,
        fileObj,
        state,
        startedAt,
        uploadCompleteCallback
      );
      if (done.restart) {
        if (restarts < 1) {
          restart(presign.uploadId, "upload_expired");
          continue;
        }
        return this._fail(
          fileObj,
          "Upload expired, please try again",
          uploadCompleteCallback
        );
      }
      return done.result;
    }
  }
}

export { DirectUploadService, generateClientUploadId, putToS3, S3PutError };
export default DirectUploadService;
